import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import * as XLSX from "xlsx";
import { useHistory } from 'react-router-dom';
import { useTranslation, usePermissions } from 'hooks';
import {
  ListUsersActions,
  ListUsersState,
  PaginateUsersActions,
  PaginateUsersState,
} from "store/ducks/settings/users";
import { SelectedFilterActions, SelectedFilterState } from 'store/ducks/filter';
import { MainContainer, Paginator, MenuSelector } from 'components/shared';
import { GridUsers, FilterUsers } from "components/settings";
import { ImpersonateModal } from 'components/settings/Users/ImpersonateModal';

import { translations } from './translations';
import * as S from './styles';

export const ListUsers: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();
  const [openModalImpersonate, setOpenModalImpersonate] = useState(false);
  const [impersonateEmail,setImpersonate] = useState("");
  const [query, setQuery] = useState<Record<string, any>>({
    limit: 10,
    page: 1,
  });

  const { data, loading, pagination } = useSelector<
    RootStateOrAny,
    PaginateUsersState
  >((state) => state.paginateUsers);

  const { loading: loadingExport } = useSelector<RootStateOrAny>(
    (state) => state.listUsers
  ) as ListUsersState;

  const { data: dataFilter } = useSelector<RootStateOrAny>(
    (state) => state.selectedFilter
  ) as SelectedFilterState;

  const exportToXLSX = (dados: any) => {
    const isSuperFilter = dados.filter((user: any) =>
     !(Array.isArray(user.groups) && user.groups.some((g: any) => g.id === 4))
    );

    if (isSuperFilter.length > 0) {
      const nameFile = getTranslation('reportFileName');
      const file = XLSX.utils.book_new();
      const wsData = isSuperFilter.map((user: any) => [
        user.id || '---',
        user.name || '---',
        user.email || '---',
        user.roles ? user.roles[0]?.name : '---',
        user?.carrier[0]?.company_name ||
          (Array.isArray(user.companies)
            ? user.companies.map((c: any) => c.trade_name).join(', ')
            : '') ||
          '---',
        (Array.isArray(user.groups)
          ? user.groups.map((c: any) => c.name).join(', ')
          : '') || '---',
        (Array.isArray(user.emails)
          ? user.emails.map((c: any) => c.email).join(', ')
          : '') || '---',
        user.last_access
          ? new Date(user.last_access).toLocaleString('pt-BR')
          : '---',
      ]);
      const worksheet = XLSX.utils.aoa_to_sheet([
        [
        'ID',
        'Nome',
        'E-mail',
        'Tipo',
        'Tipo Origem',
        'Grupo Permissões',
        'E-mail Secundário',
        'Último Acesso',
        ],
        ...wsData,
      ]);
      XLSX.utils.book_append_sheet(file, worksheet, 'Dados');
      XLSX.writeFile(file, nameFile);
    } else {
      alert('Não há dados para exportar');
    }
  };

  const getListUsers = useCallback(() => {
    dispatch(
      ListUsersActions.request(
        { all: true, ...dataFilter },
        (dados: any) => {
          exportToXLSX(dados);
        }
      )
    );
  }, [dispatch, query, dataFilter]);

  const handleSubmitFilter = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        ...value,
        page: 1,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const handlePageChange = useCallback(
    (value) => {
      const data = {
        ...dataFilter,
        page: value,
      };
      dispatch(SelectedFilterActions.success(data));
      setQuery((state) => ({ ...state, data }));
    },
    [setQuery, dispatch, dataFilter]
  );

  const getData = useCallback(() => {
    dispatch(PaginateUsersActions.request({ ...query, ...dataFilter }));
  }, [dispatch, query, dataFilter]);

  const handleImpersonate = useCallback((email: string) => {
    setImpersonate(email)
    setOpenModalImpersonate(true)
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation('configuracoes')} <MenuSelector page="users" />
          {loading && <S.LoadingPage />}
        </h1>
        <S.HeaderButtons>
          {checkUserPermission("insertUser") && (
            <S.ButtonMini onClick={getListUsers}>
              {loadingExport ? <S.LoadingPage /> : getTranslation("exportar")}
            </S.ButtonMini>
          )}
        <S.HeaderButtons/>
          {checkUserPermission('insertUser') && (
            <S.ButtonMini onClick={() => history.push('/settings/user/new')}>
              {getTranslation('novoUsuario')}
            </S.ButtonMini>
          )}
          <FilterUsers onFilter={handleSubmitFilter} />
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <GridUsers
          users={data}
          onImpersonate={handleImpersonate}
        />
        <Paginator
          pagination={pagination}
          onPageChange={handlePageChange}
          loading={loading}
        />
         {openModalImpersonate && (
          <ImpersonateModal
            openModal={openModalImpersonate}
            email={impersonateEmail}
            close={() => setOpenModalImpersonate(false)}
          />
        )}
      </S.PageContent>
    </MainContainer>
  );
};
